import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { IMAGES } from "../../data";

const ease = [0.22, 1, 0.36, 1];

const Field = ({ label, name, type = "text", value, onChange, testid, required = false }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/50">{label}{required && <span className="text-champagne"> *</span>}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border-b border-ivory/40 py-3 mt-2 text-lg font-light text-ivory focus:outline-none focus:border-b-2 transition-all"
    />
  </label>
);

const SelectField = ({ label, name, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/50">{label}</span>
    <select
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border-b border-ivory/40 py-3 mt-2 text-lg font-light text-ivory focus:outline-none focus:border-b-2 transition-all appearance-none cursor-pointer"
    >
      <option value="" className="bg-ink text-ivory">Select…</option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-ink text-ivory">{o}</option>
      ))}
    </select>
  </label>
);

const SERVICE_OPTIONS = ["Custom Gown", "Alterations", "Both", "Not sure yet"];
const LOCATION_OPTIONS = ["Perth", "Brisbane", "Other — Australia-wide"];
const REFERRAL_OPTIONS = ["Instagram", "Google", "Referral", "Facebook", "Other"];

export const Booking = ({ hideHeading = false }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", location: "",
    date: "", wedding_date: "", referral: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Thank you — we'll be in touch to confirm your appointment.");
      setForm({
        name: "", email: "", phone: "", service: "", location: "",
        date: "", wedding_date: "", referral: "", message: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please email or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-40 bg-ink text-ivory" data-testid="booking-section">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 grid md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          {!hideHeading && (
            <>
              <p className="text-xs uppercase tracking-[0.35em] text-champagne mb-6">Book Appointment</p>
              <h2 className="font-serif font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mb-10">
                Begin your fitting
              </h2>
            </>
          )}
          <p className="text-ivory/60 font-light leading-relaxed max-w-sm mb-12">
            Tell us a little about your day and Nancy will be in touch to arrange your consultation — in person or online.
          </p>
          <div className="space-y-5 text-sm">
            <a href="mailto:nkbridalaustralia@gmail.com" data-testid="contact-email" className="link-underline block w-fit">nkbridalaustralia@gmail.com</a>
            <a href="tel:0433601060" data-testid="contact-phone" className="link-underline block w-fit">0433 601 060</a>
            <a href="https://nkbridalaustralia.com.au" target="_blank" rel="noreferrer" data-testid="contact-web" className="link-underline block w-fit">nkbridalaustralia.com.au</a>
            <p className="text-ivory/50 pt-4">By appointment · Perth, WA & Australia-wide · Online consults available</p>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="md:col-span-6 md:col-start-7 grid gap-10"
          data-testid="booking-form"
        >
          <div className="grid sm:grid-cols-2 gap-10">
            <Field label="Full Name" name="name" value={form.name} onChange={onChange} testid="input-name" required />
            <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="input-email" required />
          </div>
          <div className="grid sm:grid-cols-2 gap-10">
            <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testid="input-phone" />
            <SelectField label="Location" name="location" value={form.location} onChange={onChange} options={LOCATION_OPTIONS} testid="input-location" />
          </div>
          <div className="grid sm:grid-cols-2 gap-10">
            <SelectField label="I'm interested in" name="service" value={form.service} onChange={onChange} options={SERVICE_OPTIONS} testid="input-service" />
            <SelectField label="How did you hear about us?" name="referral" value={form.referral} onChange={onChange} options={REFERRAL_OPTIONS} testid="input-referral" />
          </div>
          <div className="grid sm:grid-cols-2 gap-10">
            <Field label="Preferred appointment date" name="date" type="date" value={form.date} onChange={onChange} testid="input-date" />
            <Field label="Wedding date" name="wedding_date" type="date" value={form.wedding_date} onChange={onChange} testid="input-wedding-date" />
          </div>
          <Field label="Tell us about your day" name="message" value={form.message} onChange={onChange} testid="input-message" />
          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit"
            className="justify-self-start relative overflow-hidden border border-ivory px-10 py-4 text-xs uppercase tracking-[0.25em] group disabled:opacity-60"
          >
            <span className="relative z-10 group-hover:text-ink transition-colors duration-500">{submitting ? "Sending…" : "Request Appointment"}</span>
            <span className="absolute inset-0 bg-ivory translate-y-full group-hover:translate-y-0 transition-transform duration-500" style={{ transitionTimingFunction: "cubic-bezier(0.76,0,0.24,1)" }} />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
